import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from '@heroui/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import {
    buildDataGridSkeletonColumns,
    buildDataGridSkeletonRows,
} from '@/components';
import { Icon } from '@iconify/react';
import type { AccountingPeriod } from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    EARLIER_PERIODS_OPEN_REASON,
    formatPeriodLabel,
    formatPeriodRange,
    getPeriodActions,
    monthLabelKey,
    type LaterClosedBlock,
} from '../../periodsMeta';
import { PeriodStatusChip } from '../periodStatusChip';

type PeriodTableProps = {
    periods: AccountingPeriod[];
    /**
     * The year is still in flight. The table keeps its real column headers and
     * skeletons twelve rows — the shape the loaded screen always settles into.
     */
    isLoading?: boolean;
    /**
     * How many periods before each one are still open, keyed by period id — the
     * server closes periods strictly in order, so this is what decides whether a
     * row may close yet. Derived in `usePeriodsPage`, never here.
     */
    earlierOpenCounts?: Map<string, number>;
    /**
     * How many periods after each one are still closed, keyed by period id — the
     * server reopens periods newest-first, so this is what decides whether a
     * closed row may reopen yet. Derived in `usePeriodsPage`, never here.
     */
    laterClosedCounts?: Map<string, LaterClosedBlock>;
    /** The containing year is closed, so none of its months may move. */
    isYearSettled?: boolean;
    onClose: (period: AccountingPeriod) => void;
    onReopen: (period: AccountingPeriod) => void;
};

const SKELETON_ROW_COUNT = 12;

/**
 * The fiscal year's months, one row each: label, date range, what is inside,
 * status, and the one action the status allows.
 *
 * A HeroUI Pro `DataGrid`, like every other register on `/accounting`. The grid
 * is a real `<table>` underneath — `<th scope="col">` headers and a `<th
 * scope="row">` for the month name — so it still reads as a ledger listing. Its
 * accessible name comes from `aria-label` rather than a `<caption>`; `DataGrid`
 * exposes no caption slot.
 *
 * A row that cannot act yet states the reason instead of showing a dead control
 * — LOCKED rows, rows waiting on an earlier close, and rows waiting on a later
 * reopen all read the same way.
 */
export const PeriodTable: React.FC<PeriodTableProps> = ({
    periods,
    isLoading = false,
    earlierOpenCounts,
    laterClosedCounts,
    isYearSettled = false,
    onClose,
    onReopen,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<AccountingPeriod>[]>(
        () => [
            {
                id: 'period',
                header: t('periods.columns.period'),
                isRowHeader: true,
                minWidth: 140,
                cell: (period) => (
                    <span className="font-medium text-foreground">
                        {formatPeriodLabel(
                            period.yearMonth,
                            t(monthLabelKey(period.yearMonth)),
                        )}
                    </span>
                ),
            },
            {
                id: 'range',
                header: t('periods.columns.range'),
                minWidth: 180,
                cell: (period) => (
                    <span className="tabular-nums text-muted">
                        {formatPeriodRange(period)}
                    </span>
                ),
            },
            {
                id: 'entries',
                header: t('periods.columns.entries'),
                align: 'end',
                minWidth: 120,
                cell: (period) => (
                    <span className="tabular-nums text-foreground">
                        <span
                            {...getNodeTestIdProp(
                                `periods-entries-${period.id}`,
                            )}
                        >
                            {period.entryCount}
                        </span>
                        {period.draftCount > 0 ? (
                            <span className="ml-1.5 text-xs text-muted">
                                {t('periods.draftsInline', {
                                    count: period.draftCount,
                                })}
                            </span>
                        ) : null}
                    </span>
                ),
            },
            {
                id: 'total',
                header: t('periods.columns.total'),
                align: 'end',
                minWidth: 140,
                cell: (period) => (
                    <span className="tabular-nums text-foreground">
                        {period.postedTotalTetri > 0
                            ? formatGel(period.postedTotalTetri)
                            : '–'}
                    </span>
                ),
            },
            {
                id: 'status',
                header: t('periods.columns.status'),
                minWidth: 120,
                cell: (period) => (
                    <PeriodStatusChip slot={period.id} status={period.status} />
                ),
            },
            {
                id: 'actions',
                header: t('periods.columns.actions'),
                align: 'end',
                minWidth: 200,
                cell: (period) => {
                    const actions = getPeriodActions(period.status, {
                        earlierOpenCount: earlierOpenCounts?.get(period.id),
                        laterClosed: laterClosedCounts?.get(period.id),
                        isYearClosed: isYearSettled,
                    });
                    return (
                        <>
                            {actions.canClose ? (
                                <Button
                                    onPress={() => onClose(period)}
                                    size="sm"
                                    variant="outline"
                                    {...getNodeTestIdProp(
                                        `periods-close-${period.id}`,
                                    )}
                                >
                                    {t('periods.actions.closePeriod')}
                                </Button>
                            ) : null}
                            {actions.canReopen ? (
                                <Button
                                    onPress={() => onReopen(period)}
                                    size="sm"
                                    // Same chrome as Close: the two are
                                    // mutually exclusive per row, so matching
                                    // them keeps the column from jittering
                                    // between rows.
                                    variant="outline"
                                    {...getNodeTestIdProp(
                                        `periods-reopen-${period.id}`,
                                    )}
                                >
                                    {t('periods.actions.reopenPeriod')}
                                </Button>
                            ) : null}
                            {actions.disabledReasonKey ===
                            EARLIER_PERIODS_OPEN_REASON ? (
                                // Stated once above the table (BOS-170).
                                // Eleven copies of one rule down one page is
                                // not eleven pieces of information.
                                <Tooltip>
                                    {/* The trigger is the wrapper, not the
                                        button: react-aria does not dispatch
                                        hover or focus to a disabled control, so
                                        a tooltip attached to the Button itself
                                        never opens for a pointer or a keyboard
                                        (BOS-171). `tabIndex={0}` is what makes
                                        the reason reachable without a mouse —
                                        a disabled button is skipped by Tab, and
                                        "why can I not press this" is exactly
                                        the question a keyboard user has. */}
                                    <Tooltip.Trigger>
                                        <span
                                            className="inline-flex"
                                            tabIndex={0}
                                            {...getNodeTestIdProp(
                                                `periods-close-blocked-${period.id}`,
                                            )}
                                        >
                                            <Button
                                                isDisabled
                                                size="sm"
                                                variant="outline"
                                            >
                                                {t(
                                                    'periods.actions.closePeriod',
                                                )}
                                                <span className="sr-only">
                                                    {` — ${t('periods.reasons.closesInOrder')}`}
                                                </span>
                                            </Button>
                                        </span>
                                    </Tooltip.Trigger>
                                    <Tooltip.Content>
                                        <p>
                                            {t('periods.reasons.closesInOrder')}
                                        </p>
                                    </Tooltip.Content>
                                </Tooltip>
                            ) : actions.disabledReasonKey ? (
                                <span
                                    className="inline-flex items-center gap-1.5 text-xs text-muted"
                                    {...getNodeTestIdProp(
                                        `periods-reason-${period.id}`,
                                    )}
                                >
                                    <Icon aria-hidden icon="gravity-ui:lock" />
                                    {t(actions.disabledReasonKey, {
                                        count: actions.disabledReasonCount,
                                        period: actions.disabledReasonPeriod
                                            ? formatPeriodLabel(
                                                  actions.disabledReasonPeriod,
                                                  t(
                                                      monthLabelKey(
                                                          actions.disabledReasonPeriod,
                                                      ),
                                                  ),
                                              )
                                            : '',
                                    })}
                                </span>
                            ) : null}
                        </>
                    );
                },
            },
        ],
        [
            earlierOpenCounts,
            isYearSettled,
            laterClosedCounts,
            onClose,
            onReopen,
            t,
        ],
    );

    // `contents` wrapper: `DataGrid` forwards no unknown props, so the register's
    // testid has nowhere to land on the grid itself. A `display: contents` box
    // carries it without touching DataGrid's layout or scoped CSS.
    if (isLoading) {
        return (
            <div className="contents" {...getNodeTestIdProp('periods-table')}>
                <DataGrid<{ id: string }>
                    aria-label={t('periods.table.ariaLabel')}
                    columns={buildDataGridSkeletonColumns(columns)}
                    contentClassName="min-w-[46rem]"
                    data={buildDataGridSkeletonRows(
                        SKELETON_ROW_COUNT,
                        'periods',
                    )}
                    getRowId={(row) => row.id}
                />
            </div>
        );
    }

    return (
        <div className="contents" {...getNodeTestIdProp('periods-table')}>
            <DataGrid<AccountingPeriod>
                aria-label={t('periods.table.ariaLabel')}
                columns={columns}
                contentClassName="min-w-[46rem]"
                data={periods}
                getRowId={(period) => period.id}
            />
        </div>
    );
};
